// mediaHelpers.ts - Utility functions for blog media handling

export type BlogMediaType = "photo" | "video" | "pdf" | "audio";

export interface MediaWithHeading {
  url: string;
  heading: string;
}

export interface AudioWithHeadingAndTranscript extends MediaWithHeading {
  transcript: string;
}

export interface BlogMediaItem {
  type: BlogMediaType;
  data: MediaWithHeading | AudioWithHeadingAndTranscript;
}

export interface BlogPost {
  id?: string;
  title: string;
  content: string;
  category?: string;
  media: BlogMediaItem[];
  created_at?: string;
  updated_at?: string;
  // Legacy media fields for backward compatibility
  photos?: Array<{ url: string; caption: string }>;
  videos?: Array<{ url: string; caption: string }>;
  pdfs?: Array<{ url: string; caption: string }>;
  audios?: Array<{ url: string; caption: string; transcript: string }>;
}

// Validate media item
export function validateMediaItem(item: BlogMediaItem): string | null {
  if (!item.data.url) {
    return "Media URL is required";
  }

  if (!("heading" in item.data) || !item.data.heading) {
    return "Media heading is required";
  }

  if (item.type === "audio") {
    const audioData = item.data as AudioWithHeadingAndTranscript;
    if (!audioData.transcript) {
      return "Audio transcript is required";
    }
  }

  return null;
}

// Get media type from file
export function getMediaTypeFromFile(file: File): BlogMediaType {
  if (file.type.startsWith("image/")) return "photo";
  if (file.type.startsWith("video/")) return "video";
  if (file.type === "application/pdf") return "pdf";
  if (file.type.startsWith("audio/")) return "audio";

  // Fallback based on file extension
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || ""))
    return "photo";
  if (["mp4", "avi", "mov", "wmv"].includes(extension || "")) return "video";
  if (extension === "pdf") return "pdf";
  if (["mp3", "wav", "ogg", "opus", "m4a", "flac"].includes(extension || ""))
    return "audio";

  return "photo"; // Default fallback
}
